import React, { useEffect, useState, useCallback, useMemo } from 'react';
import authAxios from '../utils/authAxios';
import PidDisplayModal from '../components/PidDisplayModal';
import PidTableShell from '../components/PidTableShell';
import { PageWrapper, ContentWrapper } from '../styles/PackageIndexStyles';
import StatusPicker from '../components/StatusPicker';
import { PID_STATUSES, pidStatusColor, normalizePidStatus, pidStatusRank } from '../utils/pidStatus';
import { changePidStatus } from '../utils/pidStatusApi';

const formatDims = (dim) => {
  if (!dim) return '';
  const [L = '', W = '', H = ''] = String(dim).split(',').map(s => s.trim());
  if (!L && !W && !H) return '';
  return `${L} x ${W} x ${H}`;
};

const BLANK_DISPLAY = {
  UniqueID: null,
  Description: '',
  ProductID: '',
  ItemNumber: '',
  CountPerPallet: null,
  DimensionsFormed: '',
  DimensionsFlat: '',
  Images: [],
  Picture: '',
};

const PidDisplaysPage = () => {
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [selected, setSelected] = useState(null);

  const handleStatus = useCallback(async (row, next) => {
    const id = row.UniqueID;
    const prev = row.Status;
    setRows(rs => rs.map(r => (r.UniqueID === id ? { ...r, Status: next } : r)));
    try {
      await changePidStatus('displays', id, next);
    } catch (e) {
      console.error('[PidDisplays] status change failed', e);
      setRows(rs => rs.map(r => (r.UniqueID === id ? { ...r, Status: prev } : r)));
      alert(e?.response?.data?.error || 'Failed to update status.');
    }
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await authAxios.get('/api/piddisplays/index');
      setRows(res.data || []);
    } catch (err) {
      console.error('[PidDisplaysPage] Failed to load displays', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleUpdated = (updated) => {
    setRows(prev => {
      const list = Array.isArray(prev) ? prev : [];
      const byId = new Map(list.map(r => [r.UniqueID, r]));
      byId.set(updated.UniqueID, {
        ...byId.get(updated.UniqueID),
        ...updated,
      });
      return Array.from(byId.values());
    });
  };

  const columns = useMemo(() => [
    {
      key: 'Status', label: 'Status', interactive: true, cardRole: 'status',
      sortValue: (r) => pidStatusRank(r.Status),
      render: (r) => (
        <StatusPicker
          status={r.Status}
          confirm
          size="sm"
          statuses={PID_STATUSES}
          colorFor={pidStatusColor}
          normalize={normalizePidStatus}
          ariaLabel={`Status for ${r.Description || 'display'}`}
          onChange={(next) => handleStatus(r, next)}
        />
      ),
    },
    { key: 'Description', label: 'Description', emphasis: true, cardRole: 'title' },
    { key: 'ProductID', label: 'Product ID' },
    { key: 'ItemNumber', label: 'Item Number' },
    { key: 'CountPerPallet', label: 'Count (Per Pallet)', sortValue: (r) => Number(r.CountPerPallet) || 0 },
    { key: 'DimensionsFormed', label: 'Dimensions (Formed)', render: (r) => formatDims(r.DimensionsFormed), sortValue: (r) => formatDims(r.DimensionsFormed) },
    { key: 'DimensionsFlat', label: 'Dimensions (Flat)', render: (r) => formatDims(r.DimensionsFlat), sortValue: (r) => formatDims(r.DimensionsFlat) },
  ], [handleStatus]);

  const searchText = useCallback((r) => [
    r.Status, r.Description, r.ProductID, r.ItemNumber, r.CountPerPallet,
    formatDims(r.DimensionsFormed), formatDims(r.DimensionsFlat),
  ].filter(v => v !== null && v !== undefined && v !== '').join(' '), []);

  return (
    <PageWrapper>
      <ContentWrapper>
        <PidTableShell
          title="PID Displays"
          columns={columns}
          rows={rows}
          rowKey={(r) => r.UniqueID}
          searchText={searchText}
          defaultSort={{ key: 'Description', dir: 'asc' }}
          onAdd={() => setSelected({ ...BLANK_DISPLAY })}
          onRowClick={(r) => setSelected(r)}
          rowAriaLabel={(r) => `Edit display ${r.Description || ''}`}
          loading={loading}
          loadingText="Loading PID Displays..."
          emptyText="No PID Displays found."
        />

        {selected && (
          <PidDisplayModal
            group={selected}
            onClose={() => setSelected(null)}
            onUpdated={(updated) => {
              handleUpdated(updated);
              setSelected(updated);
            }}
            onDelete={async () => {
              if (!selected?.UniqueID) return;
              try {
                await authAxios.delete(`/api/piddisplays/${selected.UniqueID}`);
                setRows(prev => (prev || []).filter(r => r.UniqueID !== selected.UniqueID));
              } catch (err) {
                console.error('[PidDisplaysPage] Failed to delete display', err);
              } finally {
                setSelected(null);
              }
            }}
          />
        )}
      </ContentWrapper>
    </PageWrapper>
  );
};

export default PidDisplaysPage;
